'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaTimes,
  FaHeart,
  FaRegHeart,
  FaVolumeMute,
  FaVolumeUp,
  FaPlay,
  FaLock,
  FaMagic,
  FaAngleDoubleRight,
  FaFilm,
  FaMarker,
  FaChevronRight,
  FaListUl,
  FaDownload,
  FaUser,
  FaFlag,
} from 'react-icons/fa'
import { BsPersonBoundingBox, BsChatDotsFill } from 'react-icons/bs'
import { MdBarChart } from 'react-icons/md'
import { HiSpeakerWave } from 'react-icons/hi2'
import { FiShare } from 'react-icons/fi'

import ClipLoader from 'react-spinners/ClipLoader'
import {
  formatNumber,
  getModelName,
  isSafeCountry,
  truncate,
  downloadImage,
  downloadVideo,
} from '../../helpers/helpers'
import { checkLiked, likeImage, unlikeImage } from '../../helpers/likeApi'
import OverlayActionsMenu, { OVERLAY_PILL } from '../OverlayActionsMenu'
import useImageActions from '../useImageActions'
import ImmersiveVideo, { editUrl } from './ImmersiveVideo'
import {
  ACTION_ICON,
  ACTION_PILL,
  BOTTOM_INSET,
  CV_STYLE,
  FIT,
  TOP_RIGHT_WRAP,
} from './styles'

// Report reasons — mirrors the /image detail page.
const REPORT_REASONS = [
  'Underage content',
  'Bestiality content',
  'Celebrity or movie character',
  'Hateful content',
  'Non-consentual',
  'Broken image',
  'Other',
]

// Localized prompt, matching Card.getPrompt().
const promptFor = (item, lang) => {
  const localized =
    lang === 'ja'
      ? item?.prompt_ja
      : lang === 'de'
        ? item?.prompt_de
        : lang === 'zh'
          ? item?.prompt_zh
          : lang === 'es'
            ? item?.prompt_es
            : null
  return localized || item?.description || item?.title || ''
}

// One full-viewport TikTok/Reels page. All per-image controls (Clone/Extend,
// ellipsis menu, like/comment/share, mute) live here so they travel WITH the
// media as you swipe instead of being fixed and popping between items.
export default function Slide({
  item,
  index,
  active,
  near,
  preload,
  muted,
  onToggleMute,
  user,
  safe,
  onOpenDetails,
  likedCache,
}) {
  const { t, i18n } = useTranslation()
  const actions = useImageActions(item)

  const [paused, setPaused] = useState(false)
  // Seed from the feed-level cache so scrolling back doesn't refetch.
  const [liked, setLiked] = useState(() => likedCache.get(item.id) ?? false)
  const [likeCount, setLikeCount] = useState(
    item.likes < 0 ? 0 : item.likes || 0,
  )
  const [likeBusy, setLikeBusy] = useState(false)
  const [burstKey, setBurstKey] = useState(0)
  const [downloading, setDownloading] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [reportBusy, setReportBusy] = useState(false)
  const [reportDone, setReportDone] = useState(false)
  const [imageReady, setImageReady] = useState(false)
  const fetchedRef = useRef(false)
  const videoRef = useRef(null)

  // Toggle mute from the tap itself. This must happen inside the user-gesture call
  // stack, then sync the shared feed state (for the icon + the other slides).
  // `muted` is the current value, so a tap while muted unmutes.
  const toggleMute = () => {
    const el = videoRef.current
    if (el) {
      el.muted = !muted
      if (muted) {
        // Unmuting: the clip began as muted autoplay (started by an effect, not a
        // gesture), and iOS/Safari won't grant audio just from flipping `.muted` —
        // it only unlocks sound for a play() *initiated in this gesture*, and a
        // play() on an already-playing element is a no-op. So pause first, then
        // re-play here, which re-initiates audible playback from the tap.
        try {
          el.pause()
        } catch {
          /* ignore */
        }
        const p = el.play?.()
        if (p && p.catch) p.catch(() => {})
      }
    }
    onToggleMute()
  }

  const blurred = safe && item?.nsfw

  // Reset play state whenever this page leaves the viewport.
  useEffect(() => {
    if (!active) setPaused(false)
  }, [active])

  // Fetch liked status the first time this page becomes active — but reuse the
  // feed-level cache so re-mounting a slide on scroll-back (virtualization) does
  // NOT re-issue a check_liked_image read.
  useEffect(() => {
    if (!active || !user || blurred) return
    if (likedCache.has(item.id)) {
      setLiked(likedCache.get(item.id))
      return
    }
    if (fetchedRef.current) return
    fetchedRef.current = true
    ;(async () => {
      try {
        const token = await user.getIdToken()
        const v = await checkLiked(item.id, token)
        likedCache.set(item.id, v)
        setLiked(v)
      } catch {
        /* non-fatal */
      }
    })()
  }, [active, user, item.id, blurred, likedCache])

  const applyLike = useCallback(
    async (next) => {
      if (!user || likeBusy || next === liked) return
      setLikeBusy(true)
      setLiked(next)
      likedCache.set(item.id, next)
      setLikeCount((c) => (next ? c + 1 : Math.max(0, c - 1)))
      try {
        const token = await user.getIdToken()
        const payload = { ...item, likes: next ? likeCount + 1 : likeCount - 1 }
        if (next) await likeImage(payload, token)
        else await unlikeImage(payload, token)
      } catch {
        setLiked(!next)
        likedCache.set(item.id, !next)
        setLikeCount((c) => (next ? Math.max(0, c - 1) : c + 1))
      } finally {
        setLikeBusy(false)
      }
    },
    [user, likeBusy, liked, likeCount, item, likedCache],
  )

  const toggleLike = () => applyLike(!liked)

  const handleDownload = async () => {
    if (downloading) return
    setDownloading(true)
    try {
      const url = editUrl(item.imageUrl)
      if (item.video) await downloadVideo(url)
      else await downloadImage(url)
    } catch {
      /* helper already falls back to a direct link */
    } finally {
      setDownloading(false)
    }
  }

  const submitReport = async (reason) => {
    if (reportBusy || !user) return
    setReportBusy(true)
    try {
      const token = await user.getIdToken()
      await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/report_image`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: token },
          body: JSON.stringify({ imageData: item, reasons: [reason] }),
        },
      )
    } catch {
      /* non-fatal */
    } finally {
      setReportBusy(false)
      setReportDone(true)
    }
  }

  // Single tap: play/pause video (images do nothing). Double tap: like.
  const handleTap = () => {
    if (blurred) return
    if (item.video) setPaused((p) => !p)
  }
  const handleDoubleTap = () => {
    if (blurred || !user) return
    setBurstKey((k) => k + 1)
    if (!liked) applyLike(true)
  }

  const modelLabel = getModelName(item.model, item.video, item.videoVersion)
  const caption = promptFor(item, i18n.language)
  const showMute = item.video && item.audio_enabled
  const canShare = typeof navigator !== 'undefined' && !!navigator.share

  const menuItems = [
    ...(item.video
      ? [
          {
            key: 'clone',
            icon: <FaMagic className="text-sm" />,
            label: t('clone'),
            onClick: actions.handleClone,
          },
        ]
      : [
          {
            key: 'animate',
            icon: <FaFilm className="text-sm" />,
            label: t('animate'),
            onClick: actions.useAnimate,
          },
          {
            key: 'character',
            icon: <BsPersonBoundingBox className="text-sm" />,
            label: t('useCharacter'),
            onClick: actions.useCharacter,
          },
          {
            key: 'chat',
            icon: <BsChatDotsFill className="text-sm" />,
            label: t('chatWithThisImage'),
            onClick: actions.useChat,
          },
          {
            key: 'tweak',
            icon: <FaMarker className="text-sm" />,
            label: t('tweak', 'Tweak'),
            onClick: actions.handleTweak,
          },
        ]),
    {
      key: 'report',
      icon: <FaFlag className="text-sm" />,
      label: t('report', 'Report'),
      onClick: () => {
        setReportDone(false)
        setReportOpen(true)
      },
    },
  ]

  return (
    <div
      data-idx={index}
      className="relative flex h-[100dvh] w-full snap-start items-center justify-center"
      style={CV_STYLE}
    >
      {/* Media (tap = play/pause video; double-tap = like) */}
      <div
        className="relative flex h-full w-full items-center justify-center"
        style={{ touchAction: 'manipulation' }}
        onClick={handleTap}
        onDoubleClick={handleDoubleTap}
      >
        {item.video ? (
          <ImmersiveVideo
            item={item}
            active={active}
            near={near}
            muted={muted}
            paused={paused}
            preload={preload}
            videoRef={videoRef}
          />
        ) : (
          <img
            src={editUrl(item.imageUrl)}
            alt={truncate(caption || 'Image', 60)}
            className={`${FIT} transition-opacity duration-500 ease-out motion-reduce:transition-none ${
              imageReady ? 'opacity-100' : 'opacity-0'
            } ${blurred ? 'blur-2xl' : ''}`}
            // Preloaded neighbours fetch now; everything else stays lazy.
            loading={preload ? 'eager' : 'lazy'}
            decoding="async"
            draggable={false}
            onLoad={() => setImageReady(true)}
          />
        )}

        {item.video && paused && active && !blurred && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <FaPlay className="text-6xl text-white/80 drop-shadow-lg" />
          </div>
        )}

        {burstKey > 0 && (
          <FaHeart
            key={burstKey}
            className="pc-heart-burst pointer-events-none absolute text-7xl text-white drop-shadow-lg"
          />
        )}
      </div>

      {/* Top scrim for legibility over bright media */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-28 bg-gradient-to-b from-black/45 to-transparent" />

      {/* Safe-mode / NSFW lock — never reveal what the grid hides. Safe-country
          users have no Safe Mode toggle, so they get region wording instead of a
          label that points at a setting they can't reach. */}
      {blurred && (
        <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center">
          <FaLock className="mb-2 text-4xl text-white" />
          <span className="text-sm font-semibold text-white">
            {!user
              ? 'Sign Up to View'
              : isSafeCountry()
                ? 'Unavailable in Your Region'
                : 'Safe Mode Enabled'}
          </span>
        </div>
      )}

      {/* Top-right: primary Clone/Extend CTA + ellipsis — sticky with this slide.
          Reuses the grid card's OVERLAY_PILL / OVERLAY_ICON styling for consistency. */}
      <div
        className="@container pointer-events-none absolute inset-x-0 z-30 flex justify-end"
        style={TOP_RIGHT_WRAP}
      >
        <div className="pointer-events-auto flex items-center gap-2">
          <button
            type="button"
            aria-label={item.video ? 'Extend' : 'Clone'}
            onClick={item.video ? actions.handleExtend : actions.handleClone}
            className={OVERLAY_PILL}
          >
            {item.video ? (
              <FaAngleDoubleRight className="text-base" />
            ) : (
              <FaMagic className="text-base" />
            )}
            <span className="hidden @[380px]:inline">
              {item.video ? t('extend') : t('clone')}
            </span>
          </button>
          {/* Inline (not portaled): the portal panel renders at z-100 which would
              sit behind this z-9999 overlay; nothing clips it here. */}
          <OverlayActionsMenu items={menuItems} />
        </div>
      </div>

      {/* Bottom: caption + action row (transparent gradient, no blur) */}
      <div
        className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-24"
        style={BOTTOM_INSET}
      >
        <div className="mb-1.5 flex items-center gap-2 text-xs">
          {/* Model — tap → start a generation preset to this model */}
          <button
            type="button"
            onClick={actions.handleModel}
            className="font-semibold text-white transition active:scale-95"
          >
            {modelLabel}
          </button>
          {item.audio_enabled && (
            <span className="flex items-center gap-1 text-white/60">
              <HiSpeakerWave className="h-3 w-3" /> Audio
            </span>
          )}
          <span className="flex items-center gap-1 text-white/60">
            <MdBarChart className="text-sm" />
            {formatNumber(item.views || 0)}
          </span>
        </div>

        {/* Prompt stays short and pushed to the left (capped width), with View
            details on the right — the middle is left clear so the media shows. */}
        <div className="mb-3 flex items-center gap-3">
          {caption && (
            <button
              type="button"
              onClick={() => onOpenDetails(item.id)}
              className="min-w-0 max-w-sm flex-1 text-left"
            >
              {/* line-clamp lives on the inner span, NOT the flex <button>:
                  mobile Safari won't apply -webkit-line-clamp to a flex item /
                  a <button>, so clamping the button directly leaks the full
                  prompt. An inner block-level span clamps reliably. */}
              <span className="line-clamp-2 text-sm font-semibold leading-snug text-white/95">
                {caption}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={() => onOpenDetails(item.id)}
            className="ml-auto inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-white/20 px-4 text-[13px] font-semibold text-white ring-1 ring-white/70 transition hover:bg-white/30 active:scale-95"
          >
            <FaListUl className="text-[12px]" />
            {t('seeFullPrompt', 'See full prompt')}
            <FaChevronRight className="text-[11px]" />
          </button>
        </div>

        {/* download · share · creator · mute ......... like */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Download"
              onClick={handleDownload}
              disabled={downloading}
              className={ACTION_ICON}
            >
              {downloading ? (
                <ClipLoader color="#ffffff" size={15} />
              ) : (
                <FaDownload className="text-[15px]" />
              )}
            </button>

            {canShare && (
              <button
                type="button"
                aria-label="Share"
                onClick={() =>
                  navigator
                    .share({
                      url: `${window.location.origin}/image/${item.id}`,
                    })
                    .catch(() => {})
                }
                className={ACTION_ICON}
              >
                <FiShare className="text-[15px]" />
              </button>
            )}

            {item.uid && (
              <button
                type="button"
                aria-label={t('viewCreator', 'View creator')}
                onClick={() =>
                  window.open(
                    `/profile/${item.uid}`,
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
                className={ACTION_ICON}
              >
                <FaUser className="text-[15px]" />
              </button>
            )}

            {showMute && (
              <button
                type="button"
                aria-label={muted ? 'Unmute' : 'Mute'}
                onClick={toggleMute}
                className={ACTION_ICON}
              >
                {muted ? (
                  <FaVolumeMute className="text-[15px]" />
                ) : (
                  <FaVolumeUp className="text-[15px]" />
                )}
              </button>
            )}
          </div>

          {/* Like — isolated on the right (thumb zone) */}
          <button
            type="button"
            aria-label={liked ? 'Unlike' : 'Like'}
            onClick={toggleLike}
            disabled={!user}
            className={ACTION_PILL}
          >
            {liked ? (
              <FaHeart className="text-[15px] text-[#ff2d55]" />
            ) : (
              <FaRegHeart className="text-[15px]" />
            )}
            <span>{formatNumber(likeCount)}</span>
          </button>
        </div>
      </div>

      {/* Report sheet (opened from the ⋯ menu) */}
      {reportOpen && (
        <div
          className="absolute inset-0 z-40 flex items-end justify-center bg-black/60"
          onClick={() => setReportOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-t-2xl bg-[#0b0a0f] p-4"
            style={{
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
            }}
          >
            {reportDone ? (
              <div className="py-6 text-center text-white">
                <p className="mb-1 text-base font-semibold">
                  {t('thanksForReporting', 'Thanks for reporting')}
                </p>
                <p className="mb-4 text-sm text-white/60">
                  {t('reportReviewNote', 'Our team will review this content.')}
                </p>
                <button
                  type="button"
                  onClick={() => setReportOpen(false)}
                  className="h-10 rounded-full bg-white/15 px-6 text-sm font-semibold text-white transition hover:bg-white/25"
                >
                  {t('done', 'Done')}
                </button>
              </div>
            ) : (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-base font-semibold text-white">
                    {t('report', 'Report')}
                  </p>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={() => setReportOpen(false)}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                  >
                    <FaTimes size={16} />
                  </button>
                </div>
                <div className="flex flex-col gap-1.5">
                  {REPORT_REASONS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      disabled={reportBusy}
                      onClick={() => submitReport(r)}
                      className="flex items-center justify-between rounded-xl bg-white/5 px-4 py-3 text-left text-sm font-medium text-white transition hover:bg-white/10 disabled:opacity-50"
                    >
                      {r}
                      {reportBusy && <ClipLoader color="#ffffff" size={14} />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
